import { rootApi } from "../../../lib/api/rootApi";
import { notificationsApi } from "../../notifications/api/notificationsApi";
import { parentPortalApi } from "../../parent-portal/api/parentPortalApi";
import type {
  CreateAdhocLessonArgs,
  DistributionPlanResponse,
  LessonGuestStudent,
  PublishResult,
  SetDispositionArgs,
  SetLessonPlanArgs,
  SetStayedBackArgs,
} from "../types/distribution";

export const classDistributionPlannerApi = rootApi.injectEndpoints({
  endpoints: (builder) => ({
    getDistributionPlan: builder.query<
      DistributionPlanResponse,
      { classAbsenceId: string }
    >({
      queryFn: async ({ classAbsenceId }, _api, _extra, baseQuery) => {
        const { data, error } = await baseQuery({
          type: "rpc",
          rpc: "fn_class_absence_get_distribution_plan",
          params: { p_class_absence_id: classAbsenceId },
        });
        if (error) return { error };
        return { data: data as DistributionPlanResponse };
      },
      providesTags: (_r, _e, { classAbsenceId }) => [
        { type: "ClassDistribution", id: `plan:${classAbsenceId}` },
      ],
    }),

    setStayedBack: builder.mutation<
      { count: number },
      SetStayedBackArgs
    >({
      queryFn: async ({ classAbsenceId, studentIds }, _a, _e, baseQuery) => {
        const { data, error } = await baseQuery({
          type: "rpc",
          rpc: "fn_class_absence_set_stayed_back",
          params: {
            p_class_absence_id: classAbsenceId,
            p_student_ids: studentIds,
          },
        });
        if (error) return { error };
        return { data: { count: (data as number) ?? 0 } };
      },
      invalidatesTags: (_r, _e, { classAbsenceId }) => [
        { type: "ClassDistribution", id: `plan:${classAbsenceId}` },
      ],
    }),

    setLessonPlan: builder.mutation<{ id: string }, SetLessonPlanArgs>({
      queryFn: async (args, _a, _e, baseQuery) => {
        const { data, error } = await baseQuery({
          type: "rpc",
          rpc: "fn_class_absence_set_lesson_plan",
          params: {
            p_class_absence_id: args.classAbsenceId,
            p_course_lesson_id: args.courseLessonId,
            p_default_mode: args.defaultMode,
            p_betreuung_teacher_id: args.betreuungTeacherId ?? null,
            p_betreuung_room_id: args.betreuungRoomId ?? null,
            p_betreuung_label: args.betreuungLabel ?? null,
          },
        });
        if (error) return { error };
        return { data: { id: data as string } };
      },
      invalidatesTags: (_r, _e, { classAbsenceId }) => [
        { type: "ClassDistribution", id: `plan:${classAbsenceId}` },
      ],
    }),

    setDisposition: builder.mutation<{ id: string | null }, SetDispositionArgs>({
      queryFn: async (args, _a, _e, baseQuery) => {
        const { data, error } = await baseQuery({
          type: "rpc",
          rpc: "fn_class_absence_set_disposition",
          params: {
            p_class_absence_id: args.classAbsenceId,
            p_student_id: args.studentId,
            p_disposition: args.disposition,
            p_redirect_target_class_id: args.redirectTargetClassId ?? null,
          },
        });
        if (error) return { error };
        return { data: { id: (data as string) ?? null } };
      },
      invalidatesTags: (_r, _e, { classAbsenceId }) => [
        { type: "ClassDistribution", id: `plan:${classAbsenceId}` },
      ],
    }),

    publishDistribution: builder.mutation<
      PublishResult,
      { classAbsenceId: string }
    >({
      queryFn: async ({ classAbsenceId }, _a, _e, baseQuery) => {
        const { data, error } = await baseQuery({
          type: "rpc",
          rpc: "fn_class_absence_publish_distribution",
          params: { p_class_absence_id: classAbsenceId },
        });
        if (error) return { error };
        return { data: data as PublishResult };
      },
      invalidatesTags: (_r, _e, { classAbsenceId }) => [
        { type: "ClassDistribution", id: `plan:${classAbsenceId}` },
        { type: "ClassDistribution", id: "lesson-guests" },
        "Attendance",
      ],
    }),

    unpublishDistribution: builder.mutation<
      { deactivated: number },
      { classAbsenceId: string }
    >({
      queryFn: async ({ classAbsenceId }, _a, _e, baseQuery) => {
        const { data, error } = await baseQuery({
          type: "rpc",
          rpc: "fn_class_absence_unpublish_distribution",
          params: { p_class_absence_id: classAbsenceId },
        });
        if (error) return { error };
        return { data: { deactivated: (data as number) ?? 0 } };
      },
      invalidatesTags: (_r, _e, { classAbsenceId }) => [
        { type: "ClassDistribution", id: `plan:${classAbsenceId}` },
        { type: "ClassDistribution", id: "lesson-guests" },
        "Attendance",
      ],
    }),

    createAdhocLesson: builder.mutation<{ id: string }, CreateAdhocLessonArgs>({
      queryFn: async (args, _a, _e, baseQuery) => {
        const { data, error } = await baseQuery({
          type: "rpc",
          rpc: "fn_adhoc_lesson_create",
          params: {
            p_date: args.date,
            p_teacher_id: args.teacherId,
            p_student_ids: args.studentIds,
            p_period_id: args.periodId ?? null,
            p_start_time: args.startTime ?? null,
            p_end_time: args.endTime ?? null,
            p_room_id: args.roomId ?? null,
            p_label: args.label ?? null,
          },
        });
        if (error) return { error };
        return { data: { id: data as string } };
      },
      invalidatesTags: ["Lessons", "Timetable"],
    }),

    /**
     * Lesson-scoped: save the chosen mode without materialising guests.
     * Guest students only appear when the substitute row is published.
     */
    setLessonDistributionMode: builder.mutation<
      { id: string },
      {
        lessonId: string;
        mode: "distribute" | "stay_home" | "betreuung";
        betreuungTeacherId?: string | null;
        betreuungRoomId?: string | null;
        betreuungLabel?: string | null;
      }
    >({
      queryFn: async (args, _a, _e, baseQuery) => {
        const { data, error } = await baseQuery({
          type: "rpc",
          rpc: "fn_lesson_set_distribution_mode",
          params: {
            p_lesson_id: args.lessonId,
            p_default_mode: args.mode,
            p_betreuung_teacher_id: args.betreuungTeacherId ?? null,
            p_betreuung_room_id: args.betreuungRoomId ?? null,
            p_betreuung_label: args.betreuungLabel ?? null,
          },
        });
        if (error) return { error };
        return { data: { id: data as string } };
      },
      invalidatesTags: (_r, _e, { lessonId }) => [
        { type: "ClassDistribution", id: `lesson-plan:${lessonId}` },
      ],
    }),

    /**
     * Lesson-scoped: explicitly publish (materialise lesson_guest_students).
     * Called from the substitute-planner publish hook so guests appear at the
     * same moment as the substitute goes live.
     */
    publishLessonDistribution: builder.mutation<
      {
        lesson_id: string;
        active_guest_count: number;
        betreuung_lesson_created: number;
      },
      { lessonId: string }
    >({
      queryFn: async ({ lessonId }, _a, _e, baseQuery) => {
        const { data, error } = await baseQuery({
          type: "rpc",
          rpc: "fn_lesson_publish_distribution",
          params: { p_lesson_id: lessonId },
        });
        if (error) return { error };
        return { data: data as any };
      },
      invalidatesTags: (_r, _e, { lessonId }) => [
        { type: "ClassDistribution", id: `lesson-plan:${lessonId}` },
        { type: "ClassDistribution", id: "lesson-guests" },
        "Attendance",
        "Substitution",
      ],
    }),

    /**
     * Manual parent-decision dispatch — fired from the manage dialog's
     * "Eltern Anfragen" button. Sends the stay_home decision-request to
     * every reachable parent (auth row + allow_child_updates), idempotent
     * on (lesson, child, parent) so re-clicks won't dupe.
     */
    requestParentDecisions: builder.mutation<
      {
        lesson_id: string;
        parent_notifications_sent: number;
        stay_home_count: number;
        children_without_reachable_parent?: number;
      },
      {
        lessonId: string;
        customMessage?: string | null;
        excludedParentIds?: string[];
      }
    >({
      queryFn: async (
        { lessonId, customMessage, excludedParentIds },
        _a,
        _e,
        baseQuery,
      ) => {
        const { data, error } = await baseQuery({
          type: "rpc",
          rpc: "fn_lesson_request_parent_decisions",
          params: {
            p_lesson_id: lessonId,
            p_custom_message: customMessage ?? null,
            p_excluded_parent_ids: excludedParentIds ?? [],
          },
        });
        if (error) return { error };
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return { data: data as any };
      },
      invalidatesTags: (_r, _e, { lessonId }) => [
        { type: "ClassDistribution", id: `lesson-plan:${lessonId}` },
      ],
    }),

    /**
     * Parent reads their own decision for a (lesson, child). Used by the
     * decision dialog to flip into "you picked X — edit?" mode when reopened.
     */
    getMyDistributionDecision: builder.query<
      {
        id: string;
        decision: "go_home" | "stay_at_school";
        pickup_mode: "picked_up" | "walks_alone" | null;
        message: string | null;
        decided_at: string;
      } | null,
      { sourceLessonId: string; childProfileId: string }
    >({
      queryFn: async (
        { sourceLessonId, childProfileId },
        _a,
        _e,
        baseQuery,
      ) => {
        const { data, error } = await baseQuery({
          type: "rpc",
          rpc: "fn_parent_get_my_distribution_decision",
          params: {
            p_source_lesson_id: sourceLessonId,
            p_child_profile_id: childProfileId,
          },
        });
        if (error) return { error };
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return { data: (data as any) ?? null };
      },
      providesTags: (_r, _e, { sourceLessonId, childProfileId }) => [
        {
          type: "ClassDistribution",
          id: `parent-decision:${sourceLessonId}:${childProfileId}`,
        },
      ],
    }),

    /**
     * Parent submits their decision for a stay_home cancellation
     * (go_home + pickup_mode | stay_at_school).
     */
    setParentDistributionDecision: builder.mutation<
      { id: string },
      {
        sourceLessonId: string;
        childProfileId: string;
        decision: "go_home" | "stay_at_school";
        pickupMode?: "picked_up" | "walks_alone" | null;
        message?: string | null;
      }
    >({
      queryFn: async (args, _a, _e, baseQuery) => {
        const { data, error } = await baseQuery({
          type: "rpc",
          rpc: "fn_parent_set_distribution_decision",
          params: {
            p_source_lesson_id: args.sourceLessonId,
            p_child_profile_id: args.childProfileId,
            p_decision: args.decision,
            p_pickup_mode: args.pickupMode ?? null,
            p_message: args.message ?? null,
          },
        });
        if (error) return { error };
        return { data: { id: data as string } };
      },
      invalidatesTags: (_r, _e, { sourceLessonId, childProfileId }) => [
        {
          type: "ClassDistribution",
          id: `parent-decision:${sourceLessonId}:${childProfileId}`,
        },
      ],
      // The RPC also dismisses the parent's bell card server-side. The bell
      // history lives on a separate RTK Query API (notificationsApi), so
      // tags don't cross — we dispatch an explicit invalidation here so
      // the card disappears the moment the mutation succeeds. Realtime
      // would also eventually cover this via the dismissed_at UPDATE, but
      // the CDC listener doesn't currently react to dismissal transitions.
      async onQueryStarted(_args, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(notificationsApi.util.invalidateTags(["NotificationHistory"]));
          // Same cross-API problem: the parent-portal "Heute ausgefallene
          // Stunden" section + child-card badge live on parentPortalApi
          // (separate createApi), so explicit dispatch here too.
          dispatch(parentPortalApi.util.invalidateTags(["ParentInit"]));
        } catch {
          // mutation failed; nothing to invalidate
        }
      },
    }),

    /**
     * Parent removes their previous answer entirely. Reverts everything
     * the SET path wrote (decision row, parent-tagged flex resolution,
     * daily-log line, pickup override) and brings the bell card back
     * (un-dismisses notifications) so the parent can answer again.
     */
    deleteParentDistributionDecision: builder.mutation<
      { deleted: boolean; lesson_id: string; student_id: string },
      { sourceLessonId: string; childProfileId: string }
    >({
      queryFn: async (args, _a, _e, baseQuery) => {
        const { data, error } = await baseQuery({
          type: "rpc",
          rpc: "fn_parent_delete_distribution_decision",
          params: {
            p_source_lesson_id: args.sourceLessonId,
            p_child_profile_id: args.childProfileId,
          },
        });
        if (error) return { error };
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return { data: data as any };
      },
      invalidatesTags: (_r, _e, { sourceLessonId, childProfileId }) => [
        {
          type: "ClassDistribution",
          id: `parent-decision:${sourceLessonId}:${childProfileId}`,
        },
      ],
      // Same cross-API dispatch trick as setParentDistributionDecision —
      // the RPC un-dismisses the bell card and clears the child-detail
      // section, so we refresh both the notifications and parent-portal
      // caches.
      async onQueryStarted(_args, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(notificationsApi.util.invalidateTags(["NotificationHistory"]));
          dispatch(parentPortalApi.util.invalidateTags(["ParentInit"]));
        } catch {
          // mutation failed
        }
      },
    }),

    /**
     * Lesson-scoped read for the manage dialog. Returns full plan + per-student
     * resolved targets + conflicts + receiving-load summary.
     */
    getLessonDistributionPlan: builder.query<
      {
        lesson: {
          id: string;
          class_id: string | null;
          course_id: string | null;
          period_id: string | null;
          start_datetime: string;
          end_datetime: string;
          is_course_lesson: boolean;
          source_class_name: string | null;
          source_course_name: string | null;
          /** Lesson's period is a flex band (block_type = 'flex' OR the
           *  lesson has no class_id). */
          is_flex_band: boolean;
          /** True when the lesson is at the day's first/last instructional
           *  period for the class, or in a flex band. Used to gate Freier
           *  Abgang — only meaningful on Randstunden. */
          is_randstunde: boolean;
          /** Next flex period later the same day (drives flex-module
           *  lookup for 8.-Stunde lessons). Null when no flex band follows. */
          next_flex_period_id: string | null;
        };
        default_mode: "distribute" | "stay_home" | "betreuung" | null;
        betreuung_lesson_id: string | null;
        betreuung_teacher_id: string | null;
        betreuung_teacher_ids: string[];
        betreuung_room_id: string | null;
        betreuung_label: string | null;
        /** Section-level toggles for Freier Abgang: which choices the
         *  per-student dropdown can surface. */
        verteilung_enabled: boolean;
        betreuung_enabled: boolean;
        rooms_directory: Array<{
          room_id: string;
          name: string;
          room_number: string | null;
          /** Comma-separated list of what is already running in the same
           *  time slot, e.g. "Ma 4c, De 3b". null = room free. */
          in_use_by: string | null;
        }>;
        /** Staff IDs pinned as responsible for resolving unassigned students.
         *  Falls back to default_responsible_staff_ids (class teachers) when
         *  the plan row hasn't set its own list yet. */
        responsible_staff_ids: string[];
        /** Class teachers (is_class_teacher=true) for the source class.
         *  Used to prefill the responsible-staff selector. */
        default_responsible_staff_ids: string[];
        /** Full staff directory for the school, annotated with today's
         *  absence state so the selector can cross out absent staff. */
        staff_directory: Array<{
          staff_id: string;
          name: string;
          kuerzel: string | null;
          /** Star/UB indicator — anyone with is_class_teacher=true on at
           *  least one class link is shown with a star; everyone else
           *  is a UB (Unterstützungsbegleitung). */
          is_class_teacher: boolean;
          absent_today: boolean;
        }>;
        /** Distinct classes appearing among the in-scope roster (course-
         *  lesson mode); empty for class lessons. Each entry carries
         *  the class's is_class_teacher=true staff for the "Klassen-
         *  verantwortliche" mode of the responsible-staff selector. */
        classes_in_roster: Array<{
          class_id: string;
          class_name: string | null;
          klassenleitung_ids: string[];
        }>;
        students: Array<{
          student_id: string;
          first_name: string | null;
          last_name: string | null;
          /** Student's currently-enrolled class for the lesson date.
           *  Null when the roster comes from class_enrollments and the
           *  lesson's own class_id matches; populated for course
           *  lessons where students come from multiple classes. */
          current_class_id: string | null;
          current_class_name: string | null;
          /** When the lesson is followed by a flex band on the same day,
           *  the course this student is enrolled in for that flex period.
           *  null = no flex band, or student not enrolled. Drives the
           *  "kann nicht früher gehen" gate per student. */
          flex_module_course_id: string | null;
          flex_module_name: string | null;
          /** true when the flex lesson for this student in the next flex
           *  period is itself cancelled. The UI renders the pill struck
           *  through in red; the parent-request RPC lets the prompt go
           *  through for these students (they have nothing to attend). */
          flex_module_is_cancelled: boolean;
          override_disposition:
            | "stay_home"
            | "redirect"
            | "betreuung"
            | null;
          override_redirect_class_id: string | null;
          override_redirect_lesson_id: string | null;
          template_target_class_id: string | null;
          template_target_class_name: string | null;
          effective_disposition:
            | "distribute"
            | "stay_home"
            | "redirect"
            | "betreuung";
          resolved_target_class_id: string | null;
          resolved_target_class_name: string | null;
          resolved_target_lesson_id: string | null;
          /** Number of family-link parents with an auth.users row (push-reachable). */
          parent_auth_count: number;
          /** Total parents linked, regardless of auth state. */
          parent_total_count: number;
          /** True if a stay_home decision-request was already sent for this child on this lesson. */
          parent_notified: boolean;
          /** Full parent / guardian list for the Elternlogin column. */
          parents: Array<{
            adult_profile_id: string;
            first_name: string | null;
            last_name: string | null;
            relationship: string | null;
            /** auth.users row exists for this adult — i.e. they have a
             *  working parent-app login. */
            has_auth: boolean;
          }>;
          /** All decisions submitted by linked parents for this lesson + child. */
          parent_decisions: Array<{
            decision_id: string;
            parent_profile_id: string;
            parent_first_name: string | null;
            parent_last_name: string | null;
            decision: "go_home" | "stay_at_school";
            pickup_mode: "picked_up" | "walks_alone" | null;
            message: string | null;
            decided_at: string;
          }>;
          /** Convenience: most-recent decision (or null when no parent has answered). */
          latest_decision: "go_home" | "stay_at_school" | null;
          latest_pickup_mode: "picked_up" | "walks_alone" | null;
          latest_decided_at: string | null;
          /** Append-only thread for admin → staff notes. Backend prepends
           *  [Kürzel · DD.MM.YYYY] on every save. */
          comment: string | null;
          /** Presence_status for the lesson's date from student_daily_log.
           *  Used to render "Schüler*in heute abwesend" without a fresh
           *  per-day query. */
          daily_log_presence_status: string | null;
          daily_log_absence_note_id: string | null;
          conflict:
            | "no_target_class"
            | "target_class_absent"
            | "no_lesson_at_period"
            | null;
        }>;
        receiving_load: Array<{
          class_id: string;
          class_name: string | null;
          incoming_count: number;
        }>;
        /** Parallel flex lessons that can receive students when the source
         *  is a flex/AG lesson. Empty for class-source lessons. */
        available_redirect_lessons: Array<{
          lesson_id: string;
          course_id: string;
          course_name: string | null;
          start_datetime: string;
          end_datetime: string;
          room_name: string | null;
          teacher_last_names: string[];
        }>;
      },
      { lessonId: string }
    >({
      queryFn: async ({ lessonId }, _a, _e, baseQuery) => {
        const { data, error } = await baseQuery({
          type: "rpc",
          rpc: "fn_lesson_get_distribution_plan",
          params: { p_lesson_id: lessonId },
        });
        if (error) return { error };
        return { data: data as any };
      },
      providesTags: (_r, _e, { lessonId }) => [
        { type: "ClassDistribution", id: `lesson-plan:${lessonId}` },
      ],
    }),

    /**
     * Lesson-scoped bulk per-student disposition. Pass NULL to clear overrides
     * for the listed students. Auto-republishes after the update.
     */
    setLessonDispositionsBulk: builder.mutation<
      { updated: number; publish: { active_guest_count: number } },
      {
        lessonId: string;
        studentIds: string[];
        disposition: "stay_home" | "redirect" | "betreuung" | null;
        redirectTargetClassId?: string | null;
        redirectTargetLessonId?: string | null;
      }
    >({
      queryFn: async (args, _a, _e, baseQuery) => {
        const { data, error } = await baseQuery({
          type: "rpc",
          rpc: "fn_lesson_set_dispositions_bulk",
          params: {
            p_lesson_id: args.lessonId,
            p_student_ids: args.studentIds,
            p_disposition: args.disposition,
            p_redirect_target_class_id: args.redirectTargetClassId ?? null,
            p_redirect_target_lesson_id: args.redirectTargetLessonId ?? null,
          },
        });
        if (error) return { error };
        return { data: data as any };
      },
      invalidatesTags: (_r, _e, { lessonId }) => [
        { type: "ClassDistribution", id: `lesson-plan:${lessonId}` },
        { type: "ClassDistribution", id: "lesson-guests" },
        "Attendance",
      ],
    }),

    /**
     * Reverse the lesson-scoped distribution. Deactivates guests, drops plan/overrides.
     */
    unpublishLessonDistribution: builder.mutation<
      { deactivated_guests: number },
      { lessonId: string }
    >({
      queryFn: async ({ lessonId }, _a, _e, baseQuery) => {
        const { data, error } = await baseQuery({
          type: "rpc",
          rpc: "fn_lesson_unpublish_distribution",
          params: { p_lesson_id: lessonId },
        });
        if (error) return { error };
        return { data: data as any };
      },
      invalidatesTags: [
        { type: "ClassDistribution", id: "lesson-guests" },
        "Attendance",
        "Substitution",
      ],
    }),

    /**
     * Check whether a lesson supports the distribution flow. Eligible when:
     *   1. Regular class lesson: class_id IS NOT NULL AND course_id IS NULL, OR
     *   2. Flex course lesson:   course_id IS NOT NULL AND its schedule_period
     *                            has block_type = 'flex' (school-wide flex band
     *                            with no other classes running).
     * Returns `is_flex_course` so the picker can hide the Verteilen option for
     * case 2 — there's no destination class during a flex period.
     * RLS scopes to the caller's school via course_lessons + schedule_periods
     * select policies.
     */
    /**
     * Append a new admin comment on a (lesson, student) row. Server-side
     * formatter prepends "[Kürzel · DD.MM.YYYY] body" so the thread is
     * authoritative and forge-resistant.
     */
    setLessonDistributionComment: builder.mutation<
      unknown,
      { lessonId: string; studentId: string; comment: string }
    >({
      queryFn: async (args, _a, _e, baseQuery) => {
        const { data, error } = await baseQuery({
          type: "rpc",
          rpc: "fn_lesson_distribution_set_comment",
          params: {
            p_lesson_id: args.lessonId,
            p_student_id: args.studentId,
            p_comment: args.comment,
          },
        });
        if (error) return { error };
        return { data };
      },
      invalidatesTags: (_r, _e, { lessonId }) => [
        { type: "ClassDistribution", id: `lesson-plan:${lessonId}` },
      ],
    }),

    /**
     * Configure Freier-Abgang section options (which choices the per-row
     * dropdown surfaces, plus the Betreuung Fachkräfte + room).
     */
    setLessonDistributionSectionConfig: builder.mutation<
      unknown,
      {
        lessonId: string;
        verteilungEnabled: boolean;
        betreuungEnabled: boolean;
        betreuungTeacherIds: string[];
        betreuungRoomId: string | null;
      }
    >({
      queryFn: async (args, _a, _e, baseQuery) => {
        const { data, error } = await baseQuery({
          type: "rpc",
          rpc: "fn_lesson_distribution_set_section_config",
          params: {
            p_lesson_id: args.lessonId,
            p_verteilung_enabled: args.verteilungEnabled,
            p_betreuung_enabled: args.betreuungEnabled,
            p_betreuung_teacher_ids: args.betreuungTeacherIds,
            p_betreuung_room_id: args.betreuungRoomId,
          },
        });
        if (error) return { error };
        return { data };
      },
      invalidatesTags: (_r, _e, { lessonId }) => [
        { type: "ClassDistribution", id: `lesson-plan:${lessonId}` },
      ],
    }),

    /**
     * Set the staff who are responsible for following up on this lesson's
     * Verteilung (e.g. resolving students without a target class). Stored
     * as an array on lesson_distribution_plan.responsible_staff_ids.
     */
    setLessonDistributionResponsibleStaff: builder.mutation<
      unknown,
      { lessonId: string; staffIds: string[] }
    >({
      queryFn: async (args, _a, _e, baseQuery) => {
        const { data, error } = await baseQuery({
          type: "rpc",
          rpc: "fn_lesson_distribution_set_responsible_staff",
          params: {
            p_lesson_id: args.lessonId,
            p_staff_ids: args.staffIds,
          },
        });
        if (error) return { error };
        return { data };
      },
      invalidatesTags: (_r, _e, { lessonId }) => [
        { type: "ClassDistribution", id: `lesson-plan:${lessonId}` },
      ],
    }),

    /**
     * Admin picks "Schüler*in abwesend" in the Verteilung dropdown — creates
     * a real student_absence_notes row so the absence flows through
     * Klassenbuch / Pruefungen / daily log.
     */
    markLessonDistributionStudentAbsent: builder.mutation<
      { success: boolean; note_id: string },
      {
        lessonId: string;
        studentId: string;
        status: "inf" | "beurlaubt" | "ungeklaert";
        ganztagig: boolean;
        startTime?: string | null;
        endTime?: string | null;
        comment?: string | null;
      }
    >({
      queryFn: async (args, _a, _e, baseQuery) => {
        const { data, error } = await baseQuery({
          type: "rpc",
          rpc: "fn_lesson_distribution_mark_student_absent",
          params: {
            p_lesson_id: args.lessonId,
            p_student_id: args.studentId,
            p_status: args.status,
            p_ganztagig: args.ganztagig,
            p_start_time: args.ganztagig ? null : args.startTime ?? null,
            p_end_time: args.ganztagig ? null : args.endTime ?? null,
            p_comment: args.comment ?? null,
          },
        });
        if (error) return { error };
        return { data: data as { success: boolean; note_id: string } };
      },
      invalidatesTags: (_r, _e, { lessonId }) => [
        { type: "ClassDistribution", id: `lesson-plan:${lessonId}` },
        "Attendance",
      ],
    }),

    getLessonDistributionEligibility: builder.query<
      { eligible: boolean; is_flex_course: boolean },
      { lessonId: string }
    >({
      query: ({ lessonId }) => ({
        type: "select",
        table: "course_lessons",
        params: {
          select: "class_id,course_id,schedule_periods(block_type)",
          id: lessonId,
          single: true,
        },
      }),
      transformResponse: (raw: unknown) => {
        const row = raw as {
          class_id?: string | null;
          course_id?: string | null;
          schedule_periods?: { block_type?: string | null } | null;
        } | null;
        const isClassLesson = !!row?.class_id && !row?.course_id;
        const isFlexCourse =
          !!row?.course_id &&
          !row?.class_id &&
          row?.schedule_periods?.block_type === "flex";
        return {
          eligible: isClassLesson || isFlexCourse,
          is_flex_course: isFlexCourse,
        };
      },
    }),

    /**
     * Multi-lesson Verteilung scope for a class_absences row. Used by the
     * post-creation "Verteilung oder Abgang?" dialog and the new
     * ClassAbsenceDistributionManageDialog. Returns the affected lessons
     * + a Freier-Abgang eligibility flag computed from the school day rim.
     */
    getClassAbsenceDistributionScope: builder.query<
      {
        class_absence: {
          id: string;
          class_ids: string[];
          start_date: string;
          end_date: string;
          start_time: string | null;
          end_time: string | null;
          absence_type: string;
        };
        freier_abgang_eligible: boolean;
        first_period_start: string | null;
        last_period_end: string | null;
        classes: Array<{ class_id: string; class_name: string }>;
        lessons: Array<{
          lesson_id: string;
          class_id: string;
          class_name: string;
          start_datetime: string;
          end_datetime: string;
          period_id: string | null;
          period_label: string | null;
          block_type: string | null;
          subject_id: string | null;
          subject_name: string | null;
          is_cancelled: boolean;
        }>;
      },
      { classAbsenceId: string }
    >({
      queryFn: async ({ classAbsenceId }, _a, _e, baseQuery) => {
        const { data, error } = await baseQuery({
          type: "rpc",
          rpc: "fn_get_class_absence_distribution_scope",
          params: { p_class_absence_id: classAbsenceId },
        });
        if (error) return { error };
        return { data: data as any };
      },
      providesTags: (_r, _e, { classAbsenceId }) => [
        { type: "ClassDistribution", id: `class-absence:${classAbsenceId}` },
      ],
    }),

    /**
     * Bulk default-mode setter. Upserts a lesson_distribution_plan row
     * with the chosen mode for every lesson in the absence's scope, so
     * the existing per-lesson Verwalten dialog reads the same default.
     */
    setClassAbsenceDistributionModeBulk: builder.mutation<
      { class_absence_id: string; mode: string; lessons_updated: number },
      { classAbsenceId: string; mode: "distribute" | "stay_home" }
    >({
      queryFn: async ({ classAbsenceId, mode }, _a, _e, baseQuery) => {
        const { data, error } = await baseQuery({
          type: "rpc",
          rpc: "fn_set_class_absence_distribution_mode_bulk",
          params: { p_class_absence_id: classAbsenceId, p_mode: mode },
        });
        if (error) return { error };
        return { data: data as any };
      },
      invalidatesTags: (_r, _e, { classAbsenceId }) => [
        { type: "ClassDistribution", id: `class-absence:${classAbsenceId}` },
        "Lessons",
      ],
    }),

  }),
});

export const {
  useGetDistributionPlanQuery,
  useSetStayedBackMutation,
  useSetLessonPlanMutation,
  useSetDispositionMutation,
  usePublishDistributionMutation,
  useUnpublishDistributionMutation,
  useCreateAdhocLessonMutation,
  useGetLessonDistributionEligibilityQuery,
  useGetLessonDistributionPlanQuery,
  useSetLessonDistributionModeMutation,
  usePublishLessonDistributionMutation,
  useSetLessonDispositionsBulkMutation,
  useSetParentDistributionDecisionMutation,
  useDeleteParentDistributionDecisionMutation,
  useGetMyDistributionDecisionQuery,
  useRequestParentDecisionsMutation,
  useUnpublishLessonDistributionMutation,
  useSetLessonDistributionCommentMutation,
  useMarkLessonDistributionStudentAbsentMutation,
  useSetLessonDistributionResponsibleStaffMutation,
  useSetLessonDistributionSectionConfigMutation,
  useGetClassAbsenceDistributionScopeQuery,
  useSetClassAbsenceDistributionModeBulkMutation,
} = classDistributionPlannerApi;
